import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';

// Image URLs shown as supplementary slides on both the PDP preview pane and gallery tile carousel.
// Single source of truth — update here and both consumers stay in sync.

export const PDP_SUPPLEMENTARY_URLS = {
  // Digital
  DIGITAL_CARD_EDITOR: 'https://withjoy.com/assets/public/card-editor-preview.jpg',
  DIGITAL_SMS_DELIVERY: 'https://withjoy.com/assets/public/text-delivery-preview.jpg',
  // Basic Digital V2 -- the SMS shot on the default #F7F7F7 wallpaper, the only one the free tier gets.
  DIGITAL_BASIC_V2_DELIVERY: 'https://withjoy.com/assets/public/text-delivery-preview-basic-digital.png',
  // Taylor — card backs
  BACK_THANK_YOU: 'https://withjoy.com/assets/public/back-variety-preview-thankyoucard-2.jpg',
  BACK_THANK_YOU_POSTCARD: 'https://withjoy.com/assets/public/back-variety-preview-postcard-thankyou.jpg',
  BACK_HOLIDAY: 'https://withjoy.com/media/print/back-variety-preview-holiday.jpg',
  BACK_SAVE_DATE_INVITATION_ENCLOSURE: 'https://withjoy.com/assets/public/back-variety-preview-5.jpg',
  BACK_SAVE_DATE_POSTCARD: 'https://withjoy.com/assets/public/back-variety-preview-postcard-savethedate.jpg',
  // Taylor — envelopes & addressing
  ENVELOPES_COMMON: 'https://withjoy.com/assets/public/card-pdp-envelopes.jpg',
  ENVELOPE_THANK_YOU: 'https://withjoy.com/assets/public/envelope-variety-preview-thankyoucard-2.jpg',
  ENVELOPE_HOLIDAY: 'https://withjoy.com/media/print/envelope-variety-preview-holiday.jpg',
  ENVELOPE_SAVE_DATE_INVITATION: 'https://withjoy.com/assets/public/envelope-variety-preview-4.jpg',
} as const;

// Card selector for supplementary slides. Sizes are orientation-agnostic
// ('4x6' also matches a 6x4 landscape card); omitted sizes means every size.
export type SupplementaryImageCard = Readonly<{
  category: StationeryTemplateCategoryEnum;
  widthInches?: number;
  heightInches?: number;
  /** Active product type (e.g. 'magnet'). Magnets share the StD postcard templates
   * but are single-sided and ship in envelopes, so they never take the postcard-size branch. */
  productType?: string;
}>;

// 4x6/6x4 are the physical postcard dimensions. In the postcard categories
// (thank-you, save-the-date) a card at this size is the postcard product: a
// mailing back with stamp box + address lines, no envelope. Everywhere else
// the size means nothing special.
const POSTCARD_SIZE = '4x6';
const POSTCARD_CATEGORIES: ReadonlySet<StationeryTemplateCategoryEnum> = new Set([StationeryTemplateCategoryEnum.thankYou, StationeryTemplateCategoryEnum.saveTheDate]);

const cardSizeKey = (card: SupplementaryImageCard): string | undefined => {
  const { widthInches: w, heightInches: h } = card;
  if (w == null || h == null) return undefined;
  // Normalize orientation: 4x6 and 6x4 are the same physical card rotated.
  return [Math.min(w, h), Math.max(w, h)].join('x');
};

// The size key a card's slides are selected by. Magnets never select the
// postcard size: they share the StD postcard templates but are a different
// physical product (single-sided, ships in an envelope).
export const getSupplementaryImageSizeKey = (card: SupplementaryImageCard): string | undefined => {
  const sizeKey = cardSizeKey(card);
  if (sizeKey === POSTCARD_SIZE && card.productType === 'magnet') return undefined;
  if (sizeKey === POSTCARD_SIZE && !POSTCARD_CATEGORIES.has(card.category)) return undefined;
  return sizeKey;
};

// Returns the ordered supplementary image URLs for a given card + vendor.
// Used by the gallery tile carousel (direct URL access) and the PDP preview pane (via useCardPreviewPaneAssets).
export const getSupplementaryImages = (card: SupplementaryImageCard, vendor: 'taylor' | 'joy_digital'): readonly string[] => {
  const u = PDP_SUPPLEMENTARY_URLS;
  if (vendor === 'joy_digital') return [u.DIGITAL_CARD_EDITOR, u.DIGITAL_SMS_DELIVERY];

  // Postcard product: mailing-back art only (no envelope, no recipient addressing).
  if (getSupplementaryImageSizeKey(card) === POSTCARD_SIZE) {
    switch (card.category) {
      case StationeryTemplateCategoryEnum.thankYou:
        return [u.BACK_THANK_YOU_POSTCARD];
      case StationeryTemplateCategoryEnum.saveTheDate:
        return [u.BACK_SAVE_DATE_POSTCARD];
      default:
        return [];
    }
  }

  switch (card.category) {
    case StationeryTemplateCategoryEnum.thankYou:
      return [u.BACK_THANK_YOU, u.ENVELOPES_COMMON, u.ENVELOPE_THANK_YOU];
    case StationeryTemplateCategoryEnum.holiday:
      return [u.BACK_HOLIDAY, u.ENVELOPES_COMMON, u.ENVELOPE_HOLIDAY];
    case StationeryTemplateCategoryEnum.saveTheDate:
    case StationeryTemplateCategoryEnum.invitation:
      return [u.BACK_SAVE_DATE_INVITATION_ENCLOSURE, u.ENVELOPES_COMMON, u.ENVELOPE_SAVE_DATE_INVITATION];
    case StationeryTemplateCategoryEnum.enclosure:
      return [u.BACK_SAVE_DATE_INVITATION_ENCLOSURE];
    default:
      return [];
  }
};
